import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import Avatar from '@app/components/dashboard/Avatar';
import { Breadcrumb } from '@app/components/home/Breadcrumb';
import { Properties } from '@app/components/home/Properties';
import AreaIcon from '@app/components/icons/AreaIcon';
import ArrowDownIcon from '@app/components/icons/ArrowDownIcon';
import BathIcon from '@app/components/icons/BathIcon';
import BedIcon from '@app/components/icons/BedIcon';
import CheckedIcon from '@app/components/icons/CheckedIcon';
import CheckIcon from '@app/components/icons/CheckIcon';
import DirectionIcon from '@app/components/icons/DirectionIcon';
import FrontageIcon from '@app/components/icons/FrontageIcon';
import GridIcon from '@app/components/icons/GridIcon';
import HomeIcon from '@app/components/icons/HomeIcon';
import InfoIcon from '@app/components/icons/InfoIcon';
import ListingIcon from '@app/components/icons/ListingIcon';
import LocationIcon from '@app/components/icons/LocationIcon';
import SettingsIcon from '@app/components/icons/SettingsIcon';
import StarIcon from '@app/components/icons/StarIcon';
import VerifyIcon from '@app/components/icons/VerifyIcon';
import WarningIcon from '@app/components/icons/WarningIcon';
import TransactionModal from '@app/components/properties/TransactionModal';
import MapView from '@app/components/ui/MapView';
import Tooltip from '@app/components/ui/Tooltip';
import { withLayout } from '@app/components/withLayout';
import {
  AssetInterior,
  AssetLevel,
  LAND_DIRECTION,
  OtherPurposeType,
  PurposeType,
  TRANSACTION_STATUS,
} from '@app/constants/property';
import { useAuth } from '@app/hooks/useAuth';
import { useHeadMeta } from '@app/hooks/useHeadMeta';
import { arrayTrail, cn, humanShorten } from '@app/libs/utils';
import { administrativeService } from '@app/services/administrative.service';
import { newsService } from '@app/services/portal/news.services';
import { transactionService } from '@app/services/transaction.service';
import { ROLES } from '@app/types/listing';

const ImageCarousel = dynamic(() => import('@app/components/ui/ImageCarousel'), { ssr: false });

export const getServerSideProps: GetServerSideProps = async ctx => {
  try {
    const { id } = ctx.params as { id: string };
    console;
    const { success, data: property } = await newsService
      .setServerContext(ctx.req, ctx.res)
      .setCookies(ctx.req.headers.cookie || '')
      .detail(id);

    if (!success) {
      return { redirect: { destination: '/properties', permanent: false } };
    }

    const { data: properties, totalItem } = await newsService
      .setServerContext(ctx.req, ctx.res)
      .setCookies(ctx.req.headers.cookie || '')
      .list({
        pageIndex: 1,
        pageSize: 6,
        wardCodes: [property.wardCode],
        idIgnore: [property.id],
      });

    let transaction = null;

    if (property.publishCertificateNumber) {
      try {
        const response = await transactionService
          .setServerContext(ctx.req, ctx.res)
          .setCookies(ctx.req.headers.cookie || '')
          .search({
            keyword: property.publishCertificateNumber,
            status: 'INIT,PROCESSING',
            pageIndex: 1,
            pageSize: 1,
          });

        if (response.success) {
          transaction = response.data?.[0] ?? null;
        }
      } catch {
        transaction = null;
      }
    }

    return { props: { property, properties, totalItem, transaction } };
  } catch {
    return { redirect: { destination: '/properties', permanent: false } };
  }
};

const DIRECTION = LAND_DIRECTION.reduce(
  (acc, curr) => {
    acc[curr.value.toUpperCase()] = curr.label;
    return acc;
  },
  {} as Record<string, string>
);

const PropertyDetailPage: React.FC<{
  property: any;
  properties: any;
  totalItem: number;
  transaction: any;
}> = ({ property, properties, totalItem, transaction }) => {
  const { info: user } = useAuth();
  const { setTitle } = useHeadMeta();
  const [images, r_images] = arrayTrail([...property.images], 5);
  const [provinces, setProvinces] = useState<any>(null);
  const [allWards, setAllWards] = useState<any>(null);
  const [address, setAddress] = useState<string>();
  const [isCarouselOpen, setIsCarouselOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransactionOpen, setIsTransactionOpen] = useState(false);

  // Tổng hợp tất cả ảnh để hiển thị trong carousel
  const allImages = property.images || [];

  useEffect(() => {
    administrativeService.getAllAdministrativeUnits().then(({ provinces, wards }) => {
      setProvinces(provinces);
      setAllWards(wards);
    });
  }, []);

  useEffect(() => {
    if (!provinces || !allWards) return;
    if (property?.address) {
      let address = property.address;
      const province = provinces.find(
        (province: any) => province.provinceCode === property.provinceCode
      );
      const ward = allWards[province?.provinceCode]?.find(
        (ward: any) => ward.wardCode === property.wardCode
      );
      if (ward) {
        address = `${address}, ${ward.name}`;
      }
      if (province) {
        address = `${address}, ${province.name}`;
      }
      setAddress(address);
    }
  }, [property, provinces, allWards]);

  useEffect(() => {
    if (property?.title) {
      setTitle(property.title);
    }
  }, [property?.title, setTitle]);

  // Handler để mở carousel với ảnh được chọn
  const openCarousel = (index: number) => {
    setCurrentImageIndex(index);
    setIsCarouselOpen(true);
  };

  // Handler để đóng carousel
  const closeCarousel = () => {
    setIsCarouselOpen(false);
  };

  // Handler để chuyển ảnh tiếp theo
  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % allImages.length);
  };

  // Handler để chuyển ảnh trước đó
  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + allImages.length) % allImages.length);
  };

  // Handler để chọn ảnh từ thumbnail
  const selectImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  if (property === null || property === undefined) {
    return <div>Không tìm thấy tin đăng</div>;
  }

  return (
    <>
      <div className="bg-gray-200/80 -mb-15">
        <Breadcrumb
          items={[
            { label: 'Tin bán', href: '/properties' },
            { label: property.title, href: `/properties/${property.id}` },
          ]}
          className={cn('w-auto 2xl:w-[90rem] p-5 sm:px-6 text-gray-900 text-sm flex mx-auto')}
        />
      </div>
      <div className="w-full 2xl:w-[90rem] p-5 sm:px-6 text-gray-900 text-sm flex flex-col mx-auto gap-8 relative">
        <header className="flex flex-col items-center justify-between">
          <div className="flex items-top gap-2 w-full">
            <h2 className="text-4xl font-semibold grow">{property.title}</h2>
            <div className="flex flex-col gap-2 items-end pl-4">
              <span className="text-2xl text-blue-800 text-nowrap mt-1">
                {humanShorten(property.price)}
              </span>
              <span className="text-xs text-gray-400 text-nowrap">
                {humanShorten(Math.ceil(property.price / property.area)) || '-'}/m²
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 w-full text-gray-600">
            <LocationIcon className="w-4 h-4" />
            <span className="text-sm">{address}</span>
          </div>
        </header>

        <div className="flex items-center gap-2 w-full text-gray-600 rounded-xl overflow-hidden">
          <div className="w-full h-full grid grid-cols-5 grid-rows-2 gap-2.5">
            {images.map((image, index) => {
              // Tính toán index thực tế trong mảng allImages
              const actualIndex = index;
              return (
                <div
                  key={index}
                  onClick={() => openCarousel((image && actualIndex) || 0)}
                  className={cn(
                    'w-full relative cursor-pointer hover:opacity-90 transition-opacity',
                    index === 0 ? 'col-span-3 row-span-2 h-full' : 'aspect-square'
                  )}
                >
                  <Image
                    src={image ? `/api/v1/images/${image}` : '/images/no-image.png'}
                    alt={property.title}
                    className="w-full h-full object-cover"
                    fill
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  />

                  {index === images.length - 1 && r_images?.length > 0 && (
                    <div className="absolute top-0 left-0 w-full h-full bg-black/80 flex items-center justify-center pointer-events-none">
                      <span className="text-white text-lg">+ {r_images.length}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="w-full grid grid-cols-1 xl:grid-cols-3 gap-7.5">
          <div className="flex flex-col col-span-1 xl:col-span-2 gap-15">
            <section id="overview" className="flex flex-col gap-6">
              <h3 className="text-lg font-semibold text-gray-900">Thông tin bất động sản</h3>
              <ul className="flex justify-between w-full border border-gray-200 p-6 rounded-lg">
                <li className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <DirectionIcon className="w-6 h-6" />
                    <span className="text-gray-800 text-lg font-medium">
                      {DIRECTION[property.orientation?.toUpperCase() ?? '-']}
                    </span>
                  </div>
                  <span className="text-gray-600 text-sm">Hướng đất</span>
                </li>
                <li className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <AreaIcon className="w-6 h-6" />
                    <span className="text-gray-800 text-lg font-medium">{property.area} m²</span>
                  </div>
                  <span className="text-gray-600 text-sm">Diện tích</span>
                </li>
                <li className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <HomeIcon className="w-6 h-6" />
                    <span className="text-gray-800 text-lg font-medium">
                      {property.frontage ?? '-'} m
                    </span>
                  </div>
                  <span className="text-gray-600 text-sm">Mặt tiền</span>
                </li>
                <li className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <FrontageIcon className="w-6 h-6" />
                    <span className="text-gray-800 text-lg font-medium">
                      {property.frontage ?? '-'} m
                    </span>
                  </div>
                  <span className="text-gray-600 text-sm">Đường vào</span>
                </li>
              </ul>
            </section>

            {property?.description && (
              <section id="description" className="flex flex-col gap-6">
                <div className="flex flex-col gap-2 w-full border border-gray-200 p-6 rounded-lg">
                  <div className="text-gray-600 text-sm">{property.description || '-'}</div>
                </div>
              </section>
            )}

            {!!property &&
              (property.houseInformation.length > 0 || property.otherInformation.length > 0) && (
                <section className="flex flex-col gap-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Thông tin tài sản gắn liền với đất
                  </h3>
                  {property.houseInformation.map((info: any, index: number) => (
                    <div key={index} className="w-full border border-gray-200 p-6 rounded-lg">
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <dt className="flex items-center justify-between gap-6 col-span-2">
                          {(info?.purposes ?? ['Thông tin nhà'])
                            .map((x: any) => PurposeType[x as keyof typeof PurposeType])
                            .map((x: string, idx: number) => (
                              <span key={idx} className="text-lg font-semibold text-gray-900">
                                {x}
                              </span>
                            ))}
                        </dt>
                        {info?.description && (
                          <dd className="col-span-2 p-4 bg-gray-100 rounded-lg">
                            <div className="text-sm text-gray-700">{info.description}</div>
                          </dd>
                        )}
                        {[
                          { icon: HomeIcon, label: 'Tên căn hộ', value: info?.nameOfApartment },
                          { icon: HomeIcon, label: 'Tên chung cư', value: info?.nameOfBuilding },
                          { icon: ListingIcon, label: 'Tên dự án', value: info?.nameOfProject },
                          {
                            icon: AreaIcon,
                            label: 'Diện tích xây dựng',
                            value: info?.buildUpArea ? `${info?.buildUpArea} m²` : undefined,
                          },
                          {
                            icon: VerifyIcon,
                            label: 'Cấp công trình',
                            value: AssetLevel[info?.buildingClass as keyof typeof AssetLevel]?.name,
                          },
                          { icon: GridIcon, label: 'Số tầng', value: info?.floors },
                          { icon: BedIcon, label: 'Số phòng ngủ', value: info?.bedrooms },
                          { icon: BathIcon, label: 'Số phòng tắm', value: info?.bathrooms },
                          {
                            icon: DirectionIcon,
                            label: 'Hướng ban công',
                            value:
                              DIRECTION[info?.balconyOrientation?.toUpperCase?.() ?? ''] ??
                              info?.balconyOrientation,
                          },
                          {
                            icon: SettingsIcon,
                            label: 'Nội thất',
                            value:
                              AssetInterior[info?.furnitureType as keyof typeof AssetInterior] ??
                              info?.furnitureType,
                          },
                        ]
                          .filter(
                            item =>
                              item.value !== undefined && item.value !== null && item.value !== ''
                          )
                          .map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-6">
                              <div className="flex items-center gap-2 w-1/2">
                                {item.icon ? (
                                  <item.icon size={12} className="w-4 h-4 text-gray-500" />
                                ) : null}
                                <span className="text-xs text-gray-600">{item.label}</span>
                              </div>
                              <div className="text-sm text-gray-900 w-1/2 text-left font-medium">
                                {item.value}
                              </div>
                            </div>
                          ))}
                      </dl>
                    </div>
                  ))}

                  {property.otherInformation.map((info: any, index: number) => (
                    <div key={index} className="w-full border border-gray-200 p-6 rounded-lg">
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <dt className="flex items-center justify-between gap-6 col-span-2">
                          {(info?.purposes ?? ['Công trình khác'])
                            .map(
                              (x: any) => OtherPurposeType[x as keyof typeof OtherPurposeType] ?? x
                            )
                            .map((x: string, idx: number) => (
                              <span key={idx} className="text-lg font-semibold text-gray-900">
                                {x}
                              </span>
                            ))}
                        </dt>

                        {info?.description && (
                          <dd className="col-span-2 p-4 bg-gray-100 rounded-lg">
                            <div className="text-sm text-gray-700">{info.description}</div>
                          </dd>
                        )}

                        {[
                          {
                            icon: ListingIcon,
                            label: 'Tên công trình',
                            value: info?.nameOfConstruction,
                          },
                          { icon: GridIcon, label: 'Số tầng', value: info?.floors },
                          {
                            icon: AreaIcon,
                            label: 'Diện tích xây dựng',
                            value: info?.buildUpArea ? `${info?.buildUpArea} m²` : undefined,
                          },
                          { icon: InfoIcon, label: 'Kết cấu', value: info?.structure },
                        ]
                          .filter(
                            item =>
                              item.value !== undefined && item.value !== null && item.value !== ''
                          )
                          .map((item, idx) => (
                            <div key={idx} className="flex items-center justify-between gap-6">
                              <div className="flex items-center gap-2 w-1/2">
                                {item.icon ? (
                                  <item.icon size={12} className="w-4 h-4 text-gray-500" />
                                ) : null}
                                <span className="text-xs text-gray-600">{item.label}</span>
                              </div>
                              <div className="text-sm text-gray-900 w-1/2 text-left font-medium">
                                {item.value}
                              </div>
                            </div>
                          ))}
                      </dl>
                    </div>
                  ))}
                </section>
              )}

            {property?.centerPoint && (
              <section id="map" className="flex flex-col gap-4">
                <h3 className="text-lg font-semibold text-gray-900">Vị trí trên Bản đồ</h3>
                <div className="flex flex-col gap-2 border border-gray-200 p-3 rounded-lg min-h-[300px]">
                  <MapView
                    lat={property.centerPoint.x}
                    lng={property.centerPoint.y}
                    disableScrollZoom
                    height="300px"
                    width="100%"
                    lock
                    expandable
                  />
                </div>
              </section>
            )}

            <section id="verification" className="flex flex-col gap-4">
              <h3 className="text-lg font-semibold text-gray-900">Thông tin xác thực</h3>
              {property.isConfirmed ? (
                <div className="flex flex-col items-start gap-5 border border-green-200 bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3 text-green-600">
                    <CheckedIcon className="w-6 h-6" />
                    <span className="font-medium text-gray-900 text-xl">
                      Bất động sản đã được PropX xác thực
                    </span>
                  </div>
                  <div className="flex flex-col gap-5 text-sm text-gray-700">
                    <ul className="flex flex-col gap-3">
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-3 h-3 text-green-600" />
                        <span className="text-gray-700">Tính pháp lý sổ đỏ</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-3 h-3 text-green-600" />
                        <span className="text-gray-700">Không có tranh chấp</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-3 h-6 text-green-600" />
                        <span className="text-gray-700">Không nằm trong diện quy hoạch</span>
                      </li>
                    </ul>
                    <Link href={`/verify`}>
                      <span className="text-blue-800 text-xs italic">
                        Tìm hiểu thêm về Xác thực bất động sản
                      </span>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-start gap-5 border border-yellow-200 bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center gap-3 text-green-600">
                    <CheckedIcon className="w-6 h-6 text-gray-300" />
                    <span className="font-medium text-gray-900 text-xl">
                      Bất động sản chưa được xác thực
                    </span>
                  </div>
                  <div className="flex flex-col gap-5 text-sm text-gray-700">
                    <ul className="flex flex-col gap-3">
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-3 h-3 text-gray-300" />
                        <span className="text-gray-700">Đang kiểm tra tính pháp lý sổ đỏ</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-3 h-3 text-gray-300" />
                        <span className="text-gray-700">Đang kiểm tra không có tranh chấp</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckIcon className="w-3 h-6 text-gray-300" />
                        <span className="text-gray-700">
                          Đang kiểm tra bất động sản không nằm trong diện quy hoạch
                        </span>
                      </li>
                    </ul>
                    <Link href={`/verify`}>
                      <span className="text-blue-800 text-xs italic">
                        Tìm hiểu thêm về Xác thực bất động sản
                      </span>
                    </Link>
                  </div>
                </div>
              )}
            </section>
          </div>
          <div className="flex flex-col col-span-1">
            <div className="flex flex-col rounded-lg border border-gray-200 gap-4 p-4">
              <div className="flex gap-2 items-center text-blue-900 overflow-hidden">
                <StarIcon className="w-4 h-4" />
                <span className="text-lg font-semibold text-truncate text-ellipsis line-clamp-1">
                  {property.title}
                </span>
              </div>
              <div className="flex gap-2 items-center text-gray-600 overflow-hidden">
                <LocationIcon className="w-4 h-4" />
                <span className="text-sm text-truncate text-ellipsis line-clamp-1">
                  {property.address}
                </span>
              </div>
              <div className="flex gap-2 items-end text-gray-600 overflow-hidden">
                <span className="text-2xl text-blue-800">{humanShorten(property.price)}</span>
                <span className="text-xs text-gray-400 mb-1">
                  {humanShorten(Math.ceil(property.price / property.area)) || '-'}/m²
                </span>
              </div>

              <div className="flex gap-2">
                {property.status === 'ACTIVE' ? (
                  <span className="text-green-600 text-xs px-2 py-1 bg-green-50 border border-green-200 rounded-lg">
                    Sẵn sàng giao dịch
                  </span>
                ) : (
                  <span className="text-yellow-600 text-xs px-2 py-1 bg-yellow-50 border border-yellow-200 rounded-lg">
                    {TRANSACTION_STATUS.find(status => status.value === property.status)?.label}
                  </span>
                )}
              </div>

              {property?.canRequestTransaction && (
                <div className="flex flex-col items-start text-gray-600 overflow-hidden bg-gray-200/80 rounded-lg p-4 gap-10 mt-10">
                  <div className="flex gap-4">
                    <Avatar
                      userInfo={{
                        ...property.contactInformation,
                        avatar: property.avatar || 'text',
                      }}
                      width={60}
                      height={60}
                      className="w-15 h-15 bg-gray-300 rounded-full overflow-hidden text-xl"
                    />
                    <div className="flex flex-col gap-1 text-base overflow-hidden">
                      <span className="text-gray-800 font-medium text-lg text-truncate text-ellipsis line-clamp-1">
                        {property.contactInformation.alias ||
                          property.contactInformation.fullName ||
                          property.contactInformation.name}
                      </span>
                      <span className="text-gray-500 text-sm">
                        {' '}
                        {ROLES[property.posterType as keyof typeof ROLES]}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 items-center text-gray-600 overflow-hidden w-full">
                    {user?.isVerified ? (
                      <>
                        <Link
                          href={`tel:${property.contactInformation.phone}`}
                          className="text-blue-800 border py-2 px-5 rounded-lg border-blue-800 grow text-center cursor-pointer"
                        >
                          Liên hệ
                        </Link>
                        {property?.isConfirmed && (
                          <button
                            onClick={() => setIsTransactionOpen(true)}
                            className="text-white font-medium bg-blue-800 border py-2 px-5 rounded-lg border-blue-800 grow text-center hover:bg-blue-900 hover:border-blue-900 cursor-pointer transition-all"
                          >
                            Giao dịch
                          </button>
                        )}
                      </>
                    ) : (
                      <>
                        <Tooltip
                          content="Bạn cần đăng nhập tài khoản đã xác thực để liên hệ với người đăng tin"
                          position="top"
                          className="text-xs"
                          triggerClassName="grow"
                        >
                          <span className="text-gray-500 border py-2 px-5 rounded-lg border-gray-500 grow justify-center flex items-center gap-2">
                            <WarningIcon className="w-4 h-4 text-yellow-500" />
                            Liên hệ
                          </span>
                        </Tooltip>
                        {property?.isConfirmed && (
                          <Tooltip
                            content="Bạn cần đăng nhập tài khoản đã xác thực để tạo yêu cầu giao dịch"
                            position="top"
                            className="text-xs"
                            triggerClassName="grow"
                          >
                            <button className="text-white font-medium bg-gray-400 border py-2 px-5 rounded-lg border-gray-400 grow text-center hover:bg-gray-900 hover:border-blue-900 flex items-center justify-center gap-2 transition-all">
                              <WarningIcon className="w-4 h-4 text-yellow-500" />
                              Giao dịch
                            </button>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {user?.isVerified && (
          <TransactionModal
            isOpen={isTransactionOpen}
            onClose={() => setIsTransactionOpen(false)}
            property={property}
            closeButtonClassName="text-white"
            backdropClassName="bg-gray-900/80"
          />
        )}
      </div>

      {properties && properties.length > 0 && (
        <section id="description" className="flex flex-col gap-6 w-full items-center">
          <Link href={`/properties?wardCode=${property.wardCode}`}>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              Các tin bất động sản khác cùng khu vực
              <ArrowDownIcon size={12} className="w-4 h-4 rotate-270" />
            </h3>
          </Link>

          <Properties
            properties={properties}
            mode="scroll"
            itemClassName="w-86 max-w-[calc(100vw-2rem)]"
          />

          {totalItem > 3 && (
            <Link href={`/properties?wardCode=${property.wardCode}`}>
              <span className="text-blue-800 text-sm border border-blue-800 rounded-lg px-4 py-2 hover:bg-blue-800 hover:text-white transition-all flex items-center gap-2">
                Xem thêm
                <ArrowDownIcon size={12} className="w-4 h-4 rotate-270" />
              </span>
            </Link>
          )}
        </section>
      )}

      {/* Image Carousel */}
      <ImageCarousel
        images={allImages}
        isOpen={isCarouselOpen}
        currentIndex={currentImageIndex}
        onClose={closeCarousel}
        onNext={nextImage}
        onPrev={prevImage}
        onSelectImage={selectImage}
        alt={property.title}
      />
    </>
  );
};

export default withLayout(PropertyDetailPage);
